import { Typography, Tooltip, Dropdown, Tag } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import CustomButton from "../../../components/CustomButton/Button";
import { ENT_STATUS, BTN_TXT, FINANCING_CATEGORY_COLUMN_FILTERS, CREDIT_STATUS_DISPLAY_NAMES } from "../../../constants/constants";
import { formatCurrency, convertToPercentage } from "../../../helpers/helpers";
import { safeStringSort, safeNumberSort } from "../../../helpers/sortHelpers";
import { PERM_CODE } from "../../../constants/PermissionCodes";
import { DEAL_CATEGORY_IDS } from "../../../helpers/dealCategoryProducts";

const { Text } = Typography;

export const getCreditLimitColumns = ({
  onApprove,
  onReject,
  onDeactivate,
  onEnhance,
  onReopen,
  onReactivate,
  onView,
  status,
  privileges = [] }) => {
  let columns = [
    {
      title: "Business Name",
      dataIndex: "orgName",
      key: "orgName",
      width: 200,
      sorter: (a, b) => safeStringSort(a?.orgName, b?.orgName),
      render: (text) => text || "N/A" },
    {
      title: "Enterprise Name",
      dataIndex: "entName",
      key: "entName",
      width: 200,
      sorter: (a, b) => safeStringSort(a?.entName, b?.entName),
      render: (text) => text || "N/A" },
    {
      title: "Financing Category",
      dataIndex: "dealCategoryName",
      key: "dealCategoryName",
      width: 180,
      filters: FINANCING_CATEGORY_COLUMN_FILTERS,
      onFilter: (value, record) => record?.dealCategoryName === value,
      sorter: (a, b) => safeStringSort(a?.dealCategoryName, b?.dealCategoryName),
      render: (text, record) => {
        const categoryName = text || record?.dealCategoryName;
        const categoryId = record?.dealCategoryId;

        if (!categoryName && !categoryId) {
          return "N/A";
        }

        const normalized = (categoryName || "").toLowerCase();

        if (
          categoryId === DEAL_CATEGORY_IDS.INVOICE_FINANCING ||
          normalized.includes("invoice")
        ) {
          return <Tag color="blue">{categoryName || "Invoice Financing"}</Tag>;
        }

        if (
          categoryId === DEAL_CATEGORY_IDS.SUPPLY_CHAIN_FINANCE ||
          normalized.includes("supply chain")
        ) {
          return (
            <Tag color="purple">
              {categoryName || "Supply Chain Financing"}
            </Tag>
          );
        }

        return <Tag color="default">{categoryName}</Tag>;
      } },
    {
      title: "Industry Type",
      dataIndex: "industryTypeName",
      key: "industryTypeName",
      width: 150,
      sorter: (a, b) => safeStringSort(a?.industryTypeName, b?.industryTypeName) },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      sorter: (a, b) => {
        const statusA =
          a?.statusName ||
          CREDIT_STATUS_DISPLAY_NAMES?.[a?.busEntStatusId || a?.statusId] ||
          "";
        const statusB =
          b?.statusName ||
          CREDIT_STATUS_DISPLAY_NAMES?.[b?.busEntStatusId || b?.statusId] ||
          "";
        return safeStringSort(statusA, statusB);
      },
      render: (_, record) => {
        const statusId = record?.busEntStatusId || record?.statusId;
        const statusText =
          record?.statusName ||
          CREDIT_STATUS_DISPLAY_NAMES?.[statusId] ||
          statusId ||
          "N/A";
        return <Tag color="blue">{statusText}</Tag>;
      } },
  ];

  const statusArray = Array.isArray(status) ? status : (status ? [status] : []);
  const hasActiveOrDeactivated = statusArray.some((s) => [ENT_STATUS.ACTIVE.ID, ENT_STATUS.DEACTIVATED.ID].includes(s));
  const hasPendingOrOther = statusArray.length === 0 || statusArray.some((s) => ![ENT_STATUS.ACTIVE.ID, ENT_STATUS.DEACTIVATED.ID].includes(s));

  if (hasPendingOrOther) {
    columns.push({
      title: "Requested Credit",
      dataIndex: "requestedCredit",
      key: "requestedCredit",
      width: 180,
      sorter: (a, b) => safeNumberSort(a?.requestedCredit, b?.requestedCredit),
      render: (text) => formatCurrency(text || 0) });
  }

  if (hasActiveOrDeactivated) {
    columns.push({
      title: "Approved Credit",
      dataIndex: "approvedCredit",
      key: "approvedCredit",
      width: 180,
      sorter: (a, b) => safeNumberSort(a?.approvedCredit, b?.approvedCredit),
      render: (text) => formatCurrency(text || 0) });
  }

  columns.push(
    {
      title: "Credit Utilization (%)",
      dataIndex: "creditUtilizationPercentage",
      key: "creditUtilizationPercentage",
      width: 220,
      sorter: (a, b) => safeNumberSort(a?.creditUtilizationPercentage, b?.creditUtilizationPercentage),
      render: (text) => convertToPercentage(text || 0) },
    {
      title: "Interest Rate (Annual)",
      dataIndex: "annualDiscountRate",
      key: "annualDiscountRate",
      width: 260,
      sorter: (a, b) => safeNumberSort(a?.annualDiscountRate, b?.annualDiscountRate),
      render: (text) => convertToPercentage(text || 0) },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      width: 200,
      sorter: (a, b) => safeStringSort(a?.comments, b?.comments),
      render: (text) =>
        text ? (
          <Tooltip title={text}>
            <Text ellipsis style={{ maxWidth: 200, display: "block" }}>
              {text}
            </Text>
          </Tooltip>
        ) : (
          "N/A"
        ) },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => {
        const items = [
          {
            key: "view",
            label: "View" },
        ];

        const rowStatus =
          record?.busEntStatusId ||
          record?.statusId ||
          (statusArray.length === 1 ? statusArray[0] : null);

        // Pending
        if (rowStatus === ENT_STATUS.PENDING.ID) {
          if (privileges.includes(PERM_CODE.CPSAC)) {
            items.push({
              key: "approve",
              label: BTN_TXT.APPROVE });
          }

          if (privileges.includes(PERM_CODE.CPSRE)) {
            items.push({
              key: "reject",
              label: BTN_TXT.REJECT });
          }
        }

        // Active
        if (rowStatus === ENT_STATUS.ACTIVE.ID) {
          if (privileges.includes(PERM_CODE.CVSDE)) {
            items.push({
              key: "deactivate",
              label: BTN_TXT.DEACTIVATE });
          }

          if (privileges.includes(PERM_CODE.CVSPE)) {
            items.push({
              key: "enhance",
              label: BTN_TXT.ENHANCE });
          }
        }

        // Rejected
        if (
          rowStatus === ENT_STATUS.REJECTED.ID &&
          privileges.includes(PERM_CODE.CRSRO)
        ) {
          items.push({
            key: "reopen",
            label: BTN_TXT.REOPEN });
        }

        // Deactivated
        if (
          rowStatus === ENT_STATUS.DEACTIVATED.ID &&
          privileges.includes(PERM_CODE.CDSRE)
        ) {
          items.push({
            key: "reactivate",
            label: BTN_TXT.REACTIVATE });
        }

        const handleMenuClick = ({ key }) => {
          switch (key) {
            case "view":
              onView?.(record.busEntId, record);
              break;

            case "approve":
              onApprove?.(record);
              break;

            case "reject":
              onReject?.(record);
              break;

            case "deactivate":
              onDeactivate?.(record);
              break;

            case "enhance":
              onEnhance?.(record);
              break;

            case "reopen":
              onReopen?.(record);
              break;

            case "reactivate":
              onReactivate?.(record);
              break;

            default:
              break;
          }
        };

        return (
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            menu={{
              items,
              onClick: handleMenuClick }}
          >
            <CustomButton
              type="text"
              icon={<MoreOutlined style={{ fontSize: 18 }} />}
            />
          </Dropdown>
        );
      }
    }
  );

  return columns;
};
